import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Navbar, Footer } from "@/components/Layout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  HouseIcon, BuildingsIcon, WrenchIcon, LightningIcon, HammerIcon,
  PaintRollerIcon, WallIcon, WindIcon, HouseLineIcon, StackIcon,
  ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon, CopyIcon,
  WarningIcon, ClockIcon, CalendarBlankIcon, CameraIcon, XIcon, FileTextIcon,
  GridFourIcon, CubeIcon, GridNineIcon, DoorIcon, SprayBottleIcon,
  PottedPlantIcon, ArmchairIcon, PlugIcon, ToolboxIcon, RulerIcon,
  ThermometerSimpleIcon, DotsThreeIcon, TruckIcon, ScrewdriverIcon, TreeIcon,
} from "@phosphor-icons/react";

const CATEGORIES = [
  { name: "Plumbing", icon: WrenchIcon },
  { name: "Electrical", icon: LightningIcon },
  { name: "Carpentry", icon: HammerIcon },
  { name: "Painting", icon: PaintRollerIcon },
  { name: "Drywall", icon: WallIcon },
  { name: "HVAC", icon: WindIcon },
  { name: "Roofing", icon: HouseLineIcon },
  { name: "Flooring", icon: StackIcon },
  { name: "Masonry", icon: BuildingsIcon },
  { name: "Tile & Backsplash", icon: GridFourIcon },
  { name: "Concrete & Foundation", icon: CubeIcon },
  { name: "Fencing & Decks", icon: GridNineIcon },
  { name: "Doors & Windows", icon: DoorIcon },
  { name: "Siding & Gutters", icon: HouseIcon },
  { name: "Pressure Washing", icon: SprayBottleIcon },
  { name: "Landscaping", icon: PottedPlantIcon },
  { name: "Yard Work", icon: TreeIcon },
  { name: "Cabinetry & Countertops", icon: ArmchairIcon },
  { name: "Appliance Install & Repair", icon: PlugIcon },
  { name: "General Handyman Repairs", icon: ToolboxIcon },
  { name: "Remodeling & Renovation", icon: RulerIcon },
  { name: "Insulation & Weatherproofing", icon: ThermometerSimpleIcon },
  { name: "Delivery Service", icon: TruckIcon },
  { name: "Product Assembly", icon: ScrewdriverIcon },
  { name: "Other", icon: DotsThreeIcon },
];

const URGENCY = [
  { key: "emergency", label: "Emergency", desc: "Needs attention today", icon: WarningIcon },
  { key: "urgent", label: "Urgent", desc: "Within a few days", icon: ClockIcon },
  { key: "flexible", label: "Flexible", desc: "No rush", icon: CalendarBlankIcon },
];

const TOTAL = 4;

export default function Questionnaire() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    property_type: "",
    categories: [],
    urgency: "",
    description: "",
    preferred_date: "",
    photos: [],
    name: "",
    email: "",
    phone: "",
    address: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCat = (c) =>
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(c)
        ? f.categories.filter((x) => x !== c)
        : [...f.categories, c],
    }));

  const uploadPhotos = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(res.data.path);
      }
      setForm((prev) => ({ ...prev, photos: [...prev.photos, ...uploaded] }));
      toast.success(`${uploaded.length} photo(s) added`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const removePhoto = (p) =>
    setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== p) }));

  const [coTemplate, setCoTemplate] = useState(null);
  const [coAgree, setCoAgree] = useState(false);
  const [coName, setCoName] = useState("");
  useEffect(() => {
    api.get("/change-order-template").then((r) => setCoTemplate(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (step === 4 && !coName && form.name) setCoName(form.name);
  }, [step]); // eslint-disable-line

  const canNext = () => {
    if (step === 1) return !!form.property_type;
    if (step === 2) return form.categories.length > 0;
    if (step === 3) return form.description.trim().length > 3 && !!form.urgency;
    if (step === 4)
      return form.name && form.email && form.phone && form.address && coAgree && coName.trim();
    return true;
  };

  const next = () => {
    if (!canNext()) {
      toast.error("Please complete this step first.");
      return;
    }
    setStep((s) => Math.min(TOTAL, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!canNext()) {
      toast.error("Please fill in all contact fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/requests", { ...form, change_order_signature: coName });
      setResult(res.data);
      toast.success("Request submitted!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            data-testid="request-success"
            className="bg-white border border-slate-200 rounded-sm p-10 max-w-lg w-full text-center shadow-crisp"
          >
            <CheckCircleIcon size={64} weight="fill" className="text-green-600 mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold text-slate-900">Request received</h1>
            <p className="text-slate-600 mt-3">
              We'll review your job and reach out shortly. Save your reference ID to track status.
            </p>
            <div className="mt-7 bg-navy-900 rounded-sm px-6 py-5 flex items-center justify-between">
              <div className="text-left">
                <p className="text-xs uppercase tracking-wider text-slate-400">Reference ID</p>
                <p data-testid="request-ref" className="font-mono text-2xl font-bold text-gold-500">{result.ref}</p>
              </div>
              <button
                data-testid="copy-ref-btn"
                onClick={() => { navigator.clipboard.writeText(result.ref); toast.success("Copied!"); }}
                className="text-slate-300 hover:text-white transition-colors"
              >
                <CopyIcon size={22} />
              </button>
            </div>
            <div className="mt-7 flex gap-3 justify-center">
              <button
                data-testid="go-track-btn"
                onClick={() => navigate("/track", { state: { ref: result.ref } })}
                className="rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-6 py-3 transition-colors active:scale-95"
              >
                Track this request
              </button>
              <button
                onClick={() => navigate("/")}
                className="rounded-sm border border-slate-300 hover:border-gold-600 text-slate-900 font-semibold px-6 py-3 transition-colors"
              >
                Back home
              </button>
            </div>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <div className="flex-1 max-w-3xl mx-auto w-full px-6 py-14">
        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold-600">
              Step {step} of {TOTAL}
            </p>
            <p className="text-sm text-slate-500">{Math.round((step / TOTAL) * 100)}% complete</p>
          </div>
          <div className="h-2 bg-slate-200 rounded-sm overflow-hidden">
            <motion.div
              className="h-full bg-gold-600"
              animate={{ width: `${(step / TOTAL) * 100}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.3 }}
          >
            {step === 1 && (
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">What type of property?</h1>
                <p className="text-slate-600 mb-8">Tell us where the work needs to happen.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {[
                    { key: "residential", label: "Residential", desc: "Home, apartment or condo", icon: HouseIcon },
                    { key: "commercial", label: "Commercial", desc: "Office, retail or industrial", icon: BuildingsIcon },
                  ].map((p) => (
                    <button
                      key={p.key}
                      data-testid={`property-${p.key}`}
                      onClick={() => set("property_type", p.key)}
                      className={`text-left rounded-sm border p-6 transition-transform hover:-translate-y-1 ${
                        form.property_type === p.key
                          ? "border-gold-600 bg-gold-50 shadow-crisp-amber"
                          : "border-slate-200 bg-white hover:border-gold-600"
                      }`}
                    >
                      <p.icon size={36} weight="duotone" className="text-gold-600 mb-4" />
                      <h3 className="font-heading text-xl font-semibold text-slate-900">{p.label}</h3>
                      <p className="text-sm text-slate-600 mt-1">{p.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Which services do you need?</h1>
                <p className="text-slate-600 mb-8">Select all that apply.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {CATEGORIES.map((c) => {
                    const active = form.categories.includes(c.name);
                    return (
                      <button
                        key={c.name}
                        data-testid={`category-${c.name.toLowerCase()}`}
                        onClick={() => toggleCat(c.name)}
                        className={`rounded-sm border p-5 flex flex-col items-center gap-3 transition-transform hover:-translate-y-1 ${
                          active ? "border-gold-600 bg-gold-50 shadow-crisp-amber" : "border-slate-200 bg-white hover:border-gold-600"
                        }`}
                      >
                        <c.icon size={30} weight="duotone" className={active ? "text-gold-600" : "text-slate-700"} />
                        <span className="text-sm font-semibold text-slate-900">{c.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Describe the job</h1>
                <p className="text-slate-600 mb-8">The more detail, the faster we can help.</p>
                <div className="space-y-6">
                  <div>
                    <Label className="text-slate-900 font-medium mb-2 block">What needs to be done?</Label>
                    <Textarea
                      data-testid="input-description"
                      value={form.description}
                      onChange={(e) => set("description", e.target.value)}
                      placeholder="e.g. Kitchen sink is leaking under the cabinet and the disposal won't turn on…"
                      className="min-h-[140px] bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-900 font-medium mb-3 block">How urgent is it?</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {URGENCY.map((u) => (
                        <button
                          key={u.key}
                          data-testid={`urgency-${u.key}`}
                          onClick={() => set("urgency", u.key)}
                          className={`text-left rounded-sm border p-4 transition-colors ${
                            form.urgency === u.key ? "border-gold-600 bg-gold-50" : "border-slate-200 bg-white hover:border-gold-600"
                          }`}
                        >
                          <u.icon size={22} weight="fill" className="text-gold-600 mb-2" />
                          <p className="font-semibold text-slate-900 text-sm">{u.label}</p>
                          <p className="text-xs text-slate-500">{u.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-900 font-medium mb-2 block">Preferred date (optional)</Label>
                    <Input
                      data-testid="input-preferred-date"
                      type="date"
                      value={form.preferred_date}
                      onChange={(e) => set("preferred_date", e.target.value)}
                      className="bg-white border-slate-300 max-w-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-900 font-medium mb-2 block">Photos (optional)</Label>
                    <p className="text-sm text-slate-500 mb-3">Add pictures of the problem so we can quote accurately.</p>
                    <label
                      data-testid="photo-upload-label"
                      className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-gold-600 rounded-sm py-8 cursor-pointer transition-colors bg-white"
                    >
                      <CameraIcon size={30} weight="duotone" className="text-gold-600" />
                      <span className="text-sm font-medium text-slate-700">
                        {uploading ? "Uploading…" : "Tap to add photos"}
                      </span>
                      <input
                        data-testid="photo-input"
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }}
                      />
                    </label>
                    {form.photos.length > 0 && (
                      <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="photo-preview-grid">
                        {form.photos.map((p) => (
                          <div key={p} className="relative group aspect-square">
                            <img src={`${API}/files/${p}`} alt="upload" className="w-full h-full object-cover rounded-sm border border-slate-200" />
                            <button
                              type="button"
                              data-testid="remove-photo-btn"
                              onClick={() => removePhoto(p)}
                              className="absolute -top-2 -right-2 bg-navy-900 text-white rounded-full p-1 opacity-90 hover:bg-red-600 transition-colors"
                            >
                              <XIcon size={14} weight="bold" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Your contact info</h1>
                <p className="text-slate-600 mb-8">So we can reach you to confirm and schedule.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-slate-900 font-medium mb-2 block">Full name</Label>
                    <Input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" className="bg-white border-slate-300" />
                  </div>
                  <div>
                    <Label className="text-slate-900 font-medium mb-2 block">Phone</Label>
                    <Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 123-4567" className="bg-white border-slate-300" />
                  </div>
                  <div>
                    <Label className="text-slate-900 font-medium mb-2 block">Email</Label>
                    <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@email.com" className="bg-white border-slate-300" />
                  </div>
                  <div>
                    <Label className="text-slate-900 font-medium mb-2 block">Service address</Label>
                    <Input data-testid="input-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Main St, City, State" className="bg-white border-slate-300" />
                  </div>
                </div>
                <div className="mt-8 bg-slate-100 border border-slate-200 rounded-sm p-5">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Summary</p>
                  <div className="text-sm text-slate-700 space-y-1">
                    <p><span className="text-slate-500">Property:</span> <span className="capitalize">{form.property_type || "—"}</span></p>
                    <p><span className="text-slate-500">Services:</span> {form.categories.join(", ") || "—"}</p>
                    <p><span className="text-slate-500">Urgency:</span> <span className="capitalize">{form.urgency || "—"}</span></p>
                  </div>
                </div>

                {coTemplate && (
                  <div className="mt-6 border-2 border-gold-200 bg-gold-50/40 rounded-sm p-5" data-testid="change-order-ack">
                    <p className="font-heading font-semibold text-slate-900 flex items-center gap-2"><FileTextIcon size={18} weight="fill" className="text-gold-600" /> {coTemplate.title}</p>
                    <p className="text-sm text-slate-600 mt-1 mb-3">Please review and sign our Change Order policy before submitting your request.</p>
                    <div className="max-h-40 overflow-y-auto bg-white border border-slate-200 rounded-sm p-3 text-xs text-slate-600 space-y-2" data-testid="change-order-scroll">
                      {coTemplate.clauses.map((c) => (
                        <div key={c.heading}><span className="font-semibold text-slate-800">{c.heading}</span> — {c.body}</div>
                      ))}
                    </div>
                    <label className="flex items-start gap-2 mt-3 cursor-pointer">
                      <input type="checkbox" data-testid="change-order-agree" checked={coAgree} onChange={(e) => setCoAgree(e.target.checked)} className="mt-1" />
                      <span className="text-sm text-slate-700">I have read and agree to the Change Order Agreement.</span>
                    </label>
                    <Input data-testid="change-order-signature" value={coName} onChange={(e) => setCoName(e.target.value)} placeholder="Type your full name to sign" className="mt-3 bg-white border-slate-300 sm:max-w-xs" />
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav buttons */}
        <div className="mt-10 flex items-center justify-between">
          <button
            data-testid="step-back-btn"
            onClick={back}
            disabled={step === 1}
            className="inline-flex items-center gap-2 rounded-sm border border-slate-300 text-slate-900 font-semibold px-6 py-3 transition-colors hover:border-gold-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeftIcon size={18} weight="bold" /> Back
          </button>
          {step < TOTAL ? (
            <button
              data-testid="step-next-btn"
              onClick={next}
              className="inline-flex items-center gap-2 rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-7 py-3 transition-colors active:scale-95"
            >
              Continue <ArrowRightIcon size={18} weight="bold" />
            </button>
          ) : (
            <button
              data-testid="submit-request-btn"
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-7 py-3 transition-colors active:scale-95 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit Request"}
              {!submitting && <CheckCircleIcon size={18} weight="bold" />}
            </button>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
